import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import DataTable from '../../components/admin/DataTable';
import Modal from '../../components/admin/Modal';
import FormField from '../../components/admin/FormField';
import { usersService, rolesService, User, Role } from '../../services/admin.service';
import { Save, UserPlus, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({

    email: '',
    motdepasse: '',
    role_id: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      try {
        const usersData = await usersService.getAll();
        setUsers(usersData);
      } catch (error: any) {
        console.error('Error loading users:', error);
        toast.error('Erreur lors du chargement des utilisateurs');
      }

      try {
        const rolesData = await rolesService.getAll();
        console.log('Roles loaded from API:', rolesData);
        console.log('Roles count:', rolesData?.length || 0);

        if (rolesData && Array.isArray(rolesData) && rolesData.length > 0) {
          setRoles(rolesData);
          console.log('First role example:', {
            id: rolesData[0].id,
            name: rolesData[0].name,
            fullObject: rolesData[0]
          });
        } else {
          console.warn('No roles found or empty array:', rolesData);
          setRoles([]);
          toast.warning('Aucun rôle trouvé dans la base de données. Vérifiez que les migrations ont été exécutées.');
        }
      } catch (error: any) {
        console.error('Error loading roles:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          url: error.config?.url
        });

        setRoles([]);
        const errorMessage = error.response?.data?.message || error.message || 'Erreur lors du chargement des rôles';

        if (error.response?.status === 401) {
          toast.error('Session expirée. Veuillez vous reconnecter.');
        } else if (error.response?.status === 403) {
          toast.error('Accès refusé. Vous devez être administrateur.');
        } else {
          toast.error(`Erreur: ${errorMessage}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};



    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email invalide';
    }

    if (!editingUser && !formData.motdepasse) {
      errors.motdepasse = 'Le mot de passe est requis';
    } else if (formData.motdepasse && formData.motdepasse.length < 6) {
      errors.motdepasse = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAdd = () => {
    setEditingUser(null);
    setFormData({ email: '', motdepasse: '', role_id: '' });
    setFormErrors({});
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({

      email: user.email || '',
      motdepasse: '',
      role_id: user.role_id?.toString() || '',
    });
    setFormErrors({});
    setShowPassword(false);
    setIsModalOpen(true);
  };

  const handleDelete = async (user: User) => {
    // Prevent deletion of admin user
    if (user.id === 1) {
      toast.error('Impossible de supprimer l\'utilisateur administrateur');
      return;
    }

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${user.username}"?`)) {
      try {
        await usersService.delete(user.id);
        toast.success('Utilisateur supprimé avec succès');
        loadData();
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
        toast.error(errorMessage);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData: any = {

        email: formData.email.trim() || null,
        role_id: formData.role_id ? parseInt(formData.role_id) : null,
      };

      // Only include password if provided (for new users or when updating)
      if (formData.motdepasse) {
        submitData.motdepasse = formData.motdepasse;
      }

      if (editingUser) {
        await usersService.update(editingUser.id, submitData);
        toast.success('Utilisateur mis à jour avec succès', {
          icon: <CheckCircle2 className="text-green-500" />,
        });
      } else {
        await usersService.create(submitData);
        toast.success('Utilisateur créé avec succès', {
          icon: <CheckCircle2 className="text-green-500" />,
        });
      }
      setIsModalOpen(false);
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de l\'opération';
      toast.error(errorMessage, {
        icon: <AlertCircle className="text-red-500" />,
      });
      // Set field-specific errors if available
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
    }
  };

  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchValue.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchValue.toLowerCase())
  );

  const columns = [
    {
      key: 'id',
      header: 'ID',
    },

    {
      key: 'email',
      header: 'Email',
      render: (user: User) => user.email || <span className="text-gray-400">N/A</span>,
    },
    {
      key: 'role',
      header: 'Rôle',
      render: (user: User) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.role?.name === 'admin'
            ? 'bg-purple-100 text-purple-800'
            : user.role?.name === 'Manager'
              ? 'bg-blue-100 text-blue-800'
              : user.role?.name === 'Comptable'
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
          }`}>
          {user.role?.name || 'Aucun rôle'}
        </span>
      ),
    },
  ];

  // Show loading state immediately while data is being fetched
  if (loading && users.length === 0 && roles.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-text-secondary">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
            <UserPlus className="w-8 h-8 text-primary" />
            Gestion des Utilisateurs
          </h1>
          <p className="text-text-secondary mt-2">Gérer les utilisateurs du système et leurs rôles</p>
        </div>
        <div className="text-sm text-text-secondary">
          Total: <span className="font-semibold text-primary">{users.length}</span> utilisateur{users.length > 1 ? 's' : ''}
        </div>
      </div>

      <DataTable
        data={filteredUsers}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
        size="md"
      >
        <form onSubmit={handleSubmit}>


          <FormField label="Email">
            <input
              type="email"
              value={formData.email}
              onChange={(e) => {
                setFormData({ ...formData, email: e.target.value });
                if (formErrors.email) setFormErrors({ ...formErrors, email: '' });
              }}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors ${formErrors.email ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                }`}
              placeholder="utilisateur@example.com (optionnel)"
            />
            {formErrors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {formErrors.email}
              </p>
            )}
          </FormField>

          <FormField
            label={editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}
            required={!editingUser}
          >
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.motdepasse}
                onChange={(e) => {
                  setFormData({ ...formData, motdepasse: e.target.value });
                  if (formErrors.motdepasse) setFormErrors({ ...formErrors, motdepasse: '' });
                }}
                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors ${formErrors.motdepasse ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                  }`}
                required={!editingUser}
                placeholder={editingUser ? 'Laisser vide pour ne pas changer' : 'Minimum 6 caractères'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {formErrors.motdepasse && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {formErrors.motdepasse}
              </p>
            )}
            {!editingUser && !formErrors.motdepasse && (
              <p className="mt-1 text-xs text-gray-500">Le mot de passe doit contenir au moins 6 caractères</p>
            )}
          </FormField>

          <FormField label="Rôle">
            <select
              value={formData.role_id}
              onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              disabled={loading}
            >
              <option value="">
                {loading ? 'Chargement des rôles...' : roles.length === 0 ? 'Aucun rôle disponible' : 'Sélectionner un rôle'}
              </option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
            {roles.length === 0 && !loading && (
              <p className="mt-1 text-xs text-yellow-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Aucun rôle disponible. Veuillez créer des rôles dans la section Rôles.
              </p>
            )}
          </FormField>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-text-secondary hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{editingUser ? 'Enregistrer' : 'Créer'}</span>
            </button>
          </div>
        </form>
      </Modal>
    </motion.div>
  );
};

export default UsersPage;

